import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../config/api";
import { pages } from "../../constants/PagePaths";
import { Post } from "../../models/communityModel";
import styles from "./CommunityHub.module.scss";

interface FeedItem {
  id: number;
  title: string;
  author: string;
  createdAt: string;
  type: "qna" | "lab";
  link: string;
}

const formatDate = (dateStr: string) => {
  const date = new Date(dateStr);
  const month = date.getMonth() + 1;
  const day = date.getDate();
  return `${month}/${day}`;
};

const CommunityHub: React.FC = () => {
  const [qnaPosts, setQnaPosts] = useState<Post[]>([]);
  const [labPosts, setLabPosts] = useState<Post[]>([]);
  const [qnaTotal, setQnaTotal] = useState(0);
  const [labTotal, setLabTotal] = useState(0);
  const [labCategoryId, setLabCategoryId] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // 먼저 카테고리 목록에서 연구소 ID 찾기
        const categories = await api.getCommunityCategories();
        const labCategory = categories.find((c: any) => c.name === "연구소");
        const labId = labCategory?.id;
        if (labId) setLabCategoryId(labId);

        const promises: Promise<any>[] = [
          api.getCommunityPosts(7, 0, 3),
        ];
        if (labId) {
          promises.push(api.getCommunityPosts(labId, 0, 3));
        }

        const results = await Promise.all(promises);
        setQnaPosts(results[0].posts);
        setQnaTotal(results[0].total);
        if (results[1]) {
          setLabPosts(results[1].posts);
          setLabTotal(results[1].total);
        }
      } catch (error) {
        console.error("커뮤니티 데이터 로딩 실패:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const feedItems: FeedItem[] = [
    ...qnaPosts.map((p) => ({
      id: p.id,
      title: p.title,
      author: p.author,
      createdAt: p.createdAt,
      type: "qna" as const,
      link: `${pages.community}/7/${p.id}`,
    })),
    ...(labCategoryId
      ? labPosts.map((p) => ({
          id: p.id,
          title: p.title,
          author: p.author,
          createdAt: p.createdAt,
          type: "lab" as const,
          link: `${pages.community}/${labCategoryId}/${p.id}`,
        }))
      : []),
  ]
    .sort(
      (a, b) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    )
    .slice(0, 8);

  const getBadgeLabel = (type: string) => {
    switch (type) {
      case "qna":
        return "공무 질의응답";
      case "lab":
        return "연구소";
      default:
        return "";
    }
  };

  const getBadgeClass = (type: string) => {
    switch (type) {
      case "qna":
        return styles.badgeQna;
      case "lab":
        return styles.badgeLab;
      default:
        return "";
    }
  };

  return (
    <div>
      {/* Hero */}
      <section className={styles.hero}>
        <div className={styles.heroInner}>
          <h1 className={styles.heroTitle}>콘엑스 커뮤니티</h1>
          <p className={styles.heroSubtitle}>
            건설 실무 질문부터 AI 답변까지, 함께 나누는 공간입니다.
          </p>
        </div>
      </section>

      {/* 연구소 카드 (상단 풀와이드) */}
      {labCategoryId && (
        <section className={styles.section}>
          <Link
            to={`${pages.community}/${labCategoryId}`}
            className={styles.labCard}
          >
            <div className={styles.labCardInner}>
              <div className={styles.labCardContent}>
                <div className={styles.labCardHeader}>
                  <div className={styles.labCardIcon}>
                    <span className="material-symbols-outlined">science</span>
                  </div>
                  <div>
                    <div className={styles.labCardTitle}>
                      연구소
                      <span className={styles.labCardBadge}>NEW</span>
                    </div>
                    <p className={styles.labCardSubtitle}>
                      이런 기능 있으면 좋겠어요!
                    </p>
                  </div>
                </div>
                <p className={styles.labCardDescription}>
                  고객의 아이디어가 서비스가 됩니다. 원하는 기능, 필요한 자료
                  형식, 연락처를 남겨주시면 개발팀이 직접 검토하고 소통합니다.
                </p>
                <div className={styles.labCardCta}>
                  <span className={styles.labCardCtaButton}>
                    기능 요청하기
                    <span className="material-symbols-outlined">
                      arrow_forward
                    </span>
                  </span>
                  <span className={styles.labCardStats}>
                    {loading ? "로딩 중..." : `${labTotal}건의 요청`}
                  </span>
                </div>
              </div>
              {labPosts.length > 0 && (
                <div className={styles.labCardPreview}>
                  <div className={styles.labCardPreviewTitle}>최근 요청</div>
                  {labPosts.slice(0, 3).map((post) => (
                    <div key={post.id} className={styles.labPreviewItem}>
                      <span className={styles.previewTitle}>{post.title}</span>
                      <span className={styles.previewDate}>
                        {formatDate(post.createdAt)}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </Link>
        </section>
      )}

      {/* Category Cards */}
      <section className={styles.section} style={{ paddingTop: 0 }}>
        <h2 className={styles.sectionTitle}>게시판</h2>
        <div className={styles.categoryCards}>
          {/* 공무 질의응답 */}
          <Link
            to={`${pages.community}/7`}
            className={styles.categoryCard}
          >
            <div className={styles.cardHeader}>
              <div className={`${styles.cardIcon} ${styles.iconQna}`}>
                <span className="material-symbols-outlined">forum</span>
              </div>
              <div>
                <div className={styles.cardTitle}>공무 질의응답</div>
              </div>
            </div>
            <p className={styles.cardDescription}>
              건설 공무 관련 질문과 답변을 자유롭게 나눌 수 있는
              게시판입니다.
            </p>
            <div className={styles.cardStats}>
              {loading ? "로딩 중..." : `게시글 ${qnaTotal}건`}
            </div>
            {qnaPosts.length > 0 && (
              <div className={styles.cardPreview}>
                {qnaPosts.slice(0, 3).map((post) => (
                  <div key={post.id} className={styles.previewItem}>
                    <span className={styles.previewTitle}>{post.title}</span>
                    <span className={styles.previewDate}>
                      {formatDate(post.createdAt)}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </Link>
        </div>
      </section>

      {/* Recent Feed */}
      {feedItems.length > 0 && (
        <section className={styles.section} style={{ paddingTop: 0 }}>
          <h2 className={styles.sectionTitle}>최신글</h2>
          <div className={styles.feedList}>
            {feedItems.map((item) => (
              <Link
                key={`${item.type}-${item.id}`}
                to={item.link}
                className={styles.feedItem}
              >
                <span
                  className={`${styles.feedBadge} ${getBadgeClass(item.type)}`}
                >
                  {getBadgeLabel(item.type)}
                </span>
                <span className={styles.feedTitle}>{item.title}</span>
                <span className={styles.feedMeta}>
                  {item.author} &middot; {formatDate(item.createdAt)}
                </span>
              </Link>
            ))}
          </div>
        </section>
      )}
    </div>
  );
};

export default CommunityHub;
